import type {WixImageInfo, WixMembershipPlan} from '@wix/devzai-common-wix';
import type {CurrencyCode} from '@wix/devzai-utils-common';
import {currencyFormatPrice, currencyGetAllCodes} from '@wix/devzai-utils-common';

// Detailed pricing structure for membership plans
export interface DoppeDtoMembershipPlanDetailedPricing {
    /** Payment model type */
    paymentType: DoppeDtoMembershipPlanPaymentType;
    
    /** Basic price information */
    price?: {
        value: string;
        currency: string;
        formattedValue?: string;
    };
    
    /** For recurring plans */
    subscription?: {
        /** Length of one payment cycle */
        cycleDuration: DoppeDtoMembershipPlanDuration;
        /** Number of payment cycles (0 = unlimited) */
        cycleCount?: number | null;
    };
    
    /** For one-time payment plans with expiration */
    singlePaymentDuration?: DoppeDtoMembershipPlanDuration;
    
    /** For one-time payment plans without expiration */
    singlePaymentUnlimited?: boolean;
    
    /** Free trial period in days (only for recurring plans) */
    freeTrialDays?: number | null;
    
    /** Whether this is a free plan */
    isFree: boolean;
}

export interface DoppeDtoMembershipPlanDuration {
    /** Amount of units (currently always 1) */
    count: number;
    /** Time unit */
    unit: DoppeDtoMembershipPlanPeriodUnit;
}

export enum DoppeDtoMembershipPlanPaymentType {
    /** Free plan with no payment */
    Free = 'FREE',
    /** One-time payment with specific duration */
    OneTimeWithDuration = 'ONE_TIME_WITH_DURATION', 
    /** One-time payment, valid until canceled */
    OneTimeUnlimited = 'ONE_TIME_UNLIMITED',
    /** Recurring subscription payments */
    Recurring = 'RECURRING'
}

export enum DoppeDtoMembershipPlanPeriodUnit {
    Day = 'DAY',
    Week = 'WEEK', 
    Month = 'MONTH',
    Year = 'YEAR'
}

export interface DoppeDtoMembershipPlan {
    id: string;
    name: string;
    description?: string;
    media: WixImageInfo | null;
    membershipPageUrl?: string;
    createdDate: Date | null;
    perks: string[];
    pricing: WixMembershipPlan.Pricing | null;
    /** Extended detailed pricing information */
    detailedPricing?: DoppeDtoMembershipPlanDetailedPricing;
}

export namespace DoppeDtoMembershipPlan {
    export type Id = DoppeDtoMembershipPlan['id'];
}

export function doppeDtoMembershipPlanPaymentConvertToActionFormattedPrice<EmptyValue>(priceData: WixMembershipPlan.Pricing, emptyValue: EmptyValue): string | EmptyValue {
    const currency = priceData.price?.currency as CurrencyCode;
    const price = priceData.price?.value ? parseFloat(priceData.price?.value) : NaN;

    return !isNaN(price) && (currency && currencyGetAllCodes().includes(currency)) ? currencyFormatPrice({
        code: currency,
        price: price
    }) : (priceData.price?.formattedValue ?? emptyValue)
}

export function doppeDtoMembershipPlanGetPaymentFrequency(dto: DoppeDtoMembershipPlan): DoppeDtoMembershipPlanDuration | null {
    if (!dto.detailedPricing) {
        return null;
    }

    // Payment frequency only applies to recurring plans
    if (dto.detailedPricing.paymentType === DoppeDtoMembershipPlanPaymentType.Recurring && 
        dto.detailedPricing.subscription?.cycleDuration) {
        return dto.detailedPricing.subscription.cycleDuration;
    }

    return null;
}

export function doppeDtoMembershipPlanGetLengthOfPlan(dto: DoppeDtoMembershipPlan): DoppeDtoMembershipPlanDuration | null {
    if (!dto.detailedPricing) {
        return null;
    }

    switch (dto.detailedPricing.paymentType) {
        case DoppeDtoMembershipPlanPaymentType.OneTimeWithDuration:
            // One-time payment with specific duration
            return dto.detailedPricing.singlePaymentDuration || null;

        case DoppeDtoMembershipPlanPaymentType.Recurring:
            // For recurring plans with limited cycles, calculate total duration
            if (dto.detailedPricing.subscription?.cycleCount && 
                dto.detailedPricing.subscription.cycleCount > 0 &&
                dto.detailedPricing.subscription.cycleDuration) {
                
                const cycleDuration = dto.detailedPricing.subscription.cycleDuration;
                const totalCount = cycleDuration.count * dto.detailedPricing.subscription.cycleCount;
                
                return {
                    count: totalCount,
                    unit: cycleDuration.unit
                };
            }
            // Unlimited recurring plans return null
            return null;

        case DoppeDtoMembershipPlanPaymentType.OneTimeUnlimited:
        case DoppeDtoMembershipPlanPaymentType.Free:
        default:
            // Unlimited or free plans have no defined length
            return null;
    }
}

export function doppeDtoMembershipPlanGetLengthOfFreeTrial(dto: DoppeDtoMembershipPlan): DoppeDtoMembershipPlanDuration | null {
    if (!dto.detailedPricing || !dto.detailedPricing.freeTrialDays || dto.detailedPricing.freeTrialDays <= 0) {
        return null;
    }

    return {
        count: dto.detailedPricing.freeTrialDays,
        unit: DoppeDtoMembershipPlanPeriodUnit.Day
    };
}

